import { Box, Link, Text, VStack } from "@chakra-ui/react";
import { Form, Formik, FormikConfig, useFormikContext } from "formik";
import { FC } from "react";
import { NavLink } from "react-router-dom";

import { FormControl } from "~/src/components/common/forms/FormControl";
import { FormControlGroup } from "~/src/components/common/forms/FormControlGroup";
import { FormikFormButtons } from "~/src/components/common/forms/FormikFormButtons";
import { FormikSwitchField } from "~/src/components/common/forms/FormikSwitchField";
import { FormikTextareaField } from "~/src/components/common/forms/FormikTextareaField";
import { FormikTextField } from "~/src/components/common/forms/FormikTextField";
import { chakraFactory } from "~/src/lib/chakra-ui";

import {
  AccountReAuthenticationModal,
  Props as ModalProps,
} from "./AccountReAuthenticationModal";
import { FormValues, validationSchema } from "./validationSchema";

export type Props = {
  initialValues: FormValues;
  onCancelClick: () => void;
  onSubmit: FormikConfig<FormValues>["onSubmit"];
  modalProps: ModalProps;
  scriptTagSettingsPath?: string | null;
};

const StyledTitleText = chakraFactory(Text, {
  baseStyle: {
    fontWeight: "bold",
    fontSize: "22px",
    lineHeight: "32px",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.04em",
    color: "#282828",
  },
});

const JavaScriptTagFields: FC<{
  scriptTagSettingsPath?: string | null;
}> = ({ scriptTagSettingsPath }) => {
  const { values } = useFormikContext<FormValues>();

  return (
    <>
      <FormControl label="JavaScriptタグ管理（モジュール）">
        <FormikSwitchField name="jsTagManagementEnabled" />
      </FormControl>
      {values.jsTagManagementEnabled ? (
        <FormControl
          label="JavaScriptタグ"
          help={
            <Text>
              モジュール形式のタグはサイト設定の「タグ」タブで管理します。
              {scriptTagSettingsPath != null && (
                <>
                  <br />
                  <Link
                    as={NavLink}
                    to={scriptTagSettingsPath}
                    color="blue.500"
                    textDecoration="underline"
                  >
                    サイト設定 → タグへ移動
                  </Link>
                </>
              )}
            </Text>
          }
        >
          <Text fontSize="14px" color="#666">
            有効なため、アカウント設定からの編集はできません。
          </Text>
        </FormControl>
      ) : (
        <FormControl
          label="JavaScriptタグ"
          help={
            <Text>
              Robeeと一緒に発火させたいjavascriptコードのみを入力してください。
              <br />
              使用例： GTMをrobeeで発火させる　など
            </Text>
          }
        >
          <FormikTextareaField mode="fill" name="userTag" />
        </FormControl>
      )}
    </>
  );
};

export const AccountSettingsPage: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({
  initialValues,
  onCancelClick,
  onSubmit,
  modalProps,
  scriptTagSettingsPath,
}) => (
  <Box margin="0 auto" maxWidth="800px">
    <StyledTitleText mb="16px">アカウント設定</StyledTitleText>
    <Formik<FormValues>
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount={false}
      validateOnChange={false}
      onSubmit={onSubmit}
    >
      <Form>
        <VStack spacing="24px" alignItems="flex-start" mb="4">
          <FormControlGroup>
            <FormControl required label="会社名">
              <FormikTextField type="text" mode="fill" name="company" />
            </FormControl>
            <FormControl label="電話番号">
              <FormikTextField type="text" mode="fill" name="tel" />
            </FormControl>
            <FormControl required label="担当者名">
              <FormikTextField type="text" mode="fill" name="name" />
            </FormControl>
            <FormControl label="メールアドレス">
              <FormikTextField type="text" mode="fill" name="email" />
            </FormControl>
            <FormControl label="ITP対応用エンドポイント">
              <FormikTextField type="text" mode="fill" name="itpUrls" />
            </FormControl>
            <FormControl label="クロスドメイン(クエリ)">
              <FormikSwitchField name="isCrossDomainQuery" />
            </FormControl>
            <FormControl label="クロスドメイン(ハッシュ文字)">
              <FormikSwitchField name="isCrossDomainFragment" />
            </FormControl>
            <FormControl
              label="バックエンドURLマッピング"
              help={
                <Text>
                  オンにすると、SDKのURL変換の代わりにバックエンドのルールが適用されます。
                  ルールの検証が完了してから有効化してください。
                </Text>
              }
            >
              <FormikSwitchField name="enableBackendUrlMapping" />
            </FormControl>
            <JavaScriptTagFields
              scriptTagSettingsPath={scriptTagSettingsPath}
            />
            <AccountReAuthenticationModal {...modalProps} />
          </FormControlGroup>
          <FormikFormButtons onCancelClick={onCancelClick} />
        </VStack>
      </Form>
    </Formik>
  </Box>
);
